import { ArrowBackIos } from "@mui/icons-material";

import {
  StyledAuthButton,
  StyledAuthHeader,
  StyledAuthInputs,
} from "../../components/micro/micro.styled";
import { Box, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useWindowDimensions } from "../../utils/hooks/windowDimensionsHook";

import NewDensityLogo from "../../assets/Density app logo.svg";
import HardwareDensity from "../../assets/density-hardware.svg";

import FinalStepImage from "../../assets/Main screen active.png";
import { useDispatch } from "react-redux";
import { useEffect } from "react";
import { analyticsEvent } from "../../redux/actions/analytics";

export const UpgradeSuccessPage = () => {
  const navigate = useNavigate();
  const { width } = useWindowDimensions();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const plan = searchParams.get("plan");
  const isHardware = plan === "hardware";
  const isHardwareProBundle = plan === "hardware-pro-bundle";
  const isRTOBuyout = plan === "rto-buyout";
  const isDensityPro = plan?.startsWith("density-pro") || isHardwareProBundle || plan === "rto" || isRTOBuyout;
  
  const backButtonHandler = () => {
    navigate("/onboarding/6?instructions=true");
  };

  useEffect(() => {
    if (isHardwareProBundle) {
      dispatch(
        analyticsEvent("PAGE_VISIT", "Confirmation Page", {
          plan: "hardware-pro-bundle",
          bundleType: "hardware+pro",
        }),
      );
    } else if (isRTOBuyout) {
      dispatch(
        analyticsEvent("PAGE_VISIT", "Confirmation Page", {
          plan: "rto-buyout",
          type: "buyout",
          product: "density-pro-v1",
        }),
      );
      dispatch(
        analyticsEvent("PURCHASE", "Rent to Own Buyout Completed", {
          page: "Confirmation Page",
          plan_details: "rto-buyout",
          order_id: "TODO: Populate Order ID",
        }),
      );
    } else if (plan === "rto") { // New RTO purchase
      dispatch(
        analyticsEvent("PURCHASE", "Rent to Own Purchase Completed", {
          page: "Confirmation Page",
          plan_details: "rto",
          order_id: "TODO: Populate Order ID",
        }),
      );
    }
    else if (isDensityPro) {
      dispatch(
        analyticsEvent("PAGE_VISIT", "Confirmation Page", {
          plan: "density-pro",
          version: plan === "rto" ? "v1" : plan?.replace("density-pro-v", ""),
        }),
      );
    } else if (!isHardware) {
      dispatch(
        analyticsEvent("PAGE_VISIT", "Confirmation Page", {
          plan: "premium",
        }),
      );
    } else {
      dispatch(
        analyticsEvent("EVENT", "Hardware purchased", {
          page: "Hardware",
        }),
      );
    }
  }, [isDensityPro, isHardware, isHardwareProBundle, isRTOBuyout, plan, dispatch]);

  const handleButtonClick = () => {
    if (searchParams.get("fromDesktop") === "true") {
      window.open("density://", "_blank");
      // Add a delay before redirecting to allow user to see the success message
      setTimeout(() => {
        backButtonHandler();
      }, 3000); // 3 second delay
    } else {
      backButtonHandler();
    }
  };

  const steps = ["1", "2", "3", "4"];

  return (
    <div className="flex h-[100%] w-full flex-col overflow-hidden bg-[#141519] md:justify-between lg:h-full">
      <StyledAuthHeader>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            maxWidth: "500px",
            mx: "auto",
            position: "relative",
          }}
        >
          <Box
            sx={{
              cursor: "pointer",
              position: "absolute",
              left: "0",
            }}
            onClick={backButtonHandler}
          >
            <ArrowBackIos />
          </Box>
          <img
            onClick={() => navigate("/")}
            style={{ width: "175px", height: "54px", objectFit: "contain" }}
            src={NewDensityLogo}
            alt={"Density"}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: "10px",
            gap: "10px",
            justifyContent: "space-between",
            maxWidth: "500px",
          }}
          mt={2}
          mx={"auto"}
        >
          {steps.map((stepNumber: string) => (
            <Box
              key={stepNumber}
              sx={{
                py: "8px",
                width: width! <= 500 ? "45px" : "106px",
                borderRadius: "4px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <Box
                sx={{
                  height: "3px",
                  flexShrink: 0,
                  width: width! <= 500 ? "45px" : "106px",
                  borderRadius: "4px",
                  backgroundColor: "#fcfcfc",
                }}
              ></Box>
            </Box>
          ))}
        </Box>
      </StyledAuthHeader>

      <div className="flex h-[100%] flex-col items-center justify-between gap-8 lg:flex-row">
        <div className="h-[100%] min-w-max">
          <StyledAuthInputs
            sx={{
              margin: "auto",
              justifyContent: "center",
            }}
          >
            <Box>
              <Typography
                textAlign={"center"}
                fontSize={"20px"}
                variant={"h5"}
                mb={2}
                fontWeight={500}
                color={"rgba(252, 252, 252, 1)"}
                marginTop={8}
              >
                {isHardwareProBundle
                  ? "Your Hardware + Pro Bundle is confirmed!"
                  : isHardware
                  ? "Your order is on the way!"
                  : isRTOBuyout
                  ? "You now have lifetime access to Density Pro v1!"
                  : isDensityPro
                  ? `You're on Density Pro ${plan?.includes('v1') || plan === 'rto' ? 'v1' : ''}!`
                  : "You're on Density Premium!"}
              </Typography>
              {(isHardware || isDensityPro || isHardwareProBundle) && (
                <Typography
                  fontSize={"12px"}
                  fontWeight={500}
                  textAlign={"center"}
                  variant={"body1"}
                  color={"rgba(252, 252, 252, 0.6)"}
                  maxWidth={275}
                  mx={"auto"}
                  lineHeight={"18px"}
                  letterSpacing={"0.1px"}
                >
                  {isHardwareProBundle
                    ? "Your hardware is on the way and Density Pro is now active! Download the app to access all Pro features."
                    : isHardware
                    ? "While you wait, download the Density Desktop app to learn how to edit a set."
                    : isRTOBuyout
                    ? "Congratulations on completing your Rent-to-Own purchase! You now have lifetime access to Density Pro."
                    : "Thank you for purchasing Density Pro! Download the app to access all Pro features."}
                </Typography>
              )}
            </Box>
            <Box sx={{ mt: "45px" }}>
              <StyledAuthButton
                onClick={handleButtonClick}
                variant={"contained"}
                sx={{
                  mx: 0,
                  mb: 2,
                }}
              >
                {searchParams.get("fromDesktop") === "true"
                  ? "Open Density App"
                  : "Download"}
              </StyledAuthButton>
            </Box>
          </StyledAuthInputs>
        </div>
        <div className="mx-auto">
          {(isHardware || isHardwareProBundle) ? (
            <img
              className="sm-translate-x-[44px] -translate-x-[20px]  overflow-clip object-contain md:-translate-x-[56px]"
              src={HardwareDensity}
              alt="REC-1 Hardware"
            />
          ) : (
            <img src={FinalStepImage} alt="REC-1 Hardware" className="" />
          )}
        </div>
      </div>
    </div>
  );
};
